import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Outlet, useLocation } from 'react-router-dom';
import { Loader, Spinner } from '@oliasoft-open-source/react-ui-library';
import { ability, ActionMap } from '@oliasoft/authorization-ui';
import { FeatureFlags } from '@oliasoft/feature-flags';
import { TopBar } from 'views/navigation/top-bar/top-bar';
import { getUserSettings } from 'store/entities/user-settings/user-settings';
import { getCompanySettings, getCompanyCurrencyTemplates } from 'store/entities/company-settings/company-settings';
import { getCompanies } from 'store/entities/companies/companies';
import { getAbility } from 'store/entities/authorization/authorization';
import { getUser, getUsers } from 'src/store/entities/user/user';
import { getActiveTemplate } from 'store/entities/unit-templates/unit-templates';
import { getCompanyTemplates, getUserTemplates } from 'src/store/entities/templates/templates';
import { useSocket, WEBSOCKET_EVENTS } from 'src/common/socket-contex/socket-context';
import { CaslEnums } from 'src/enums/casl-enums';
import { NotFound } from 'views/not-found/not-found';
import { FeatureFlagName } from 'src/enums/feature-flags';

const Main = ({
  getUserSettings,
  getCompanySettings,
  getCompanyTemplates,
  getUserTemplates,
  getCompanies,
  getUser,
  getUsers,
  getAbility,
  getActiveTemplate,
  isFetching,
  companies,
  getCompanyCurrencyTemplates,
}) => {
  const { socket, isConnected } = useSocket();
  const { company: companyId } = useParams();
  const { pathname } = useLocation();
  const isUserSettings = pathname?.includes('/user-settings');
  const isProjectCurrencySettingsActive = FeatureFlags.isActive(FeatureFlagName.UserSetCurrency);

  useEffect(() => {
    getCompanies();
    getUser();
  }, []);

  useEffect(() => {
    if (companyId) {
      const company = companies.find((company) => company.id === companyId);
      if (company) {
        sessionStorage.setItem('activeCompany', JSON.stringify(company));
        window.dispatchEvent(new Event('storage'));

        getCompanySettings(companyId);
        getCompanyTemplates(companyId);
        getUserTemplates();
        getAbility(companyId);
        getUsers();
        if (isProjectCurrencySettingsActive) {
          getCompanyCurrencyTemplates();
        }
        getActiveTemplate();
      }
    }
    getUserSettings();
  }, [companyId, companies, isProjectCurrencySettingsActive]);

  useEffect(() => {
    if (isFetching) {
      return;
    }
    const payload = {
      scope: 'GROUP',
      value: companyId,
    };

    if (isConnected) {
      socket.emit(WEBSOCKET_EVENTS.change_scope, payload);
    }
  }, [isConnected, isFetching]);

  const settingsLoader =
    isFetching || companies.length === 0 ? (
      <Loader fullViewPortSize text="Loading...">
        <Spinner />
      </Loader>
    ) : null;

  const hasPermissionToSeeContent = ability.can(ActionMap.READ, CaslEnums.Access);

  return (
    <>
      {settingsLoader || (
        <>
          <TopBar />
          {hasPermissionToSeeContent || isUserSettings ? <Outlet /> : <NotFound />}
        </>
      )}
    </>
  );
};

const mapStateToProps = ({ entities }) => ({
  isFetching:
    entities.userSettings.isFetching ||
    entities.authorization.isFetching ||
    entities.unitTemplates.isFetching ||
    entities.companySettings.currencyTemplatesFetching,
  companies: entities.companies.list,
});

const mapDispatchToProps = {
  getUserSettings,
  getCompanySettings,
  getAbility,
  getCompanies,
  getUser,
  getUsers,
  getActiveTemplate,
  getCompanyTemplates,
  getUserTemplates,
  getCompanyCurrencyTemplates,
};

const Container = connect(mapStateToProps, mapDispatchToProps)(Main);

export { Container as Main };
